<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { VIcon } from '@/modules/shared/components'
import { Briefcase } from 'lucide-vue-next'

// ─── Configuration ──────────────────────────────────────────────────────────
// All tunable parameters live here. Adjust these to change the feel.

const CONFIG = {
  // Ring geometry
  ringCount: 6,
  baseRadius: 60,
  ringRatio: 1.55, // exponential growth: r(n) = baseRadius * ratio^n
  ringOffsets: [0, 0, 30, 15, 45, 20], // starting angle offset per ring (degrees)
  initialOrbsPerRing: [0, 1, 2, 3, 5, 7], // index 0 unused (center ring)

  // Orb population
  maxOrbs: 25,
  orbSize: 44, // px
  linearSpeed: 12, // px/s — constant across all rings

  // Initial entrance animation
  enterBaseDelay: 800, // ms before first orb appears
  enterStagger: 60, // ms between each orb entrance
  enterFadeDuration: 400, // ms fade-in per orb

  // Click-to-spawn spring
  spawnStiffness: 180,
  spawnDamping: 16,
  spawnSettleThreshold: 0.5,

  // Eviction (FIFO overflow)
  evictionFadeMs: 300,

  // ─── Lifecycle state machine ───────────────────────────────────────────
  // Minimum orbit time before an orb is eligible to be matched
  minOrbitTime: 5000,

  // Ring weights for match selection (index 0 unused, 1=innermost)
  // Higher = more likely to be selected
  ringWeights: [0, 5, 4, 3, 2, 1],

  // State durations [min, max] in ms — randomized per orb
  matchedDuration: [2000, 4000],
  acceptingDelay: [1500, 3000], // time in approved state before starting spiral absorption

  // Matched/approved orbs slow to this fraction of their orbital speed
  matchedSpeedFactor: 0.7,

  // Spiral absorption (angular momentum conservation)
  spiralPullBase: 50, // px/s — base inward pull speed
  spiralPullAccel: 80, // additional pull as orb approaches center (px/s at r=0)
  spiralMomentumBoost: 1.8, // multiply captured angular momentum to exaggerate spiral
  spiralMaxAngularSpeed: 12, // cap ω so inner spiral doesn't go crazy
  spiralFadeRadius: 30, // px from center where shrink+fade begins

  // ─── Matching probability ────────────────────────────────────────────────
  // Probability-based matching: each eligible orb is checked periodically
  matchCheckInterval: 300, // ms between probability rolls
  ambientMatchChance: 0.003, // base chance per orb per check (outside waves)

  // Wave windows — periodic bursts of elevated match probability (bell curve)
  waveInterval: [15000, 25000], // ms between wave centers [min, max]
  waveWindowDuration: [800, 1500], // how long the window stays open [min, max]
  wavePeakBoost: 0.025, // match chance added at peak of bell curve
  waveBellTightness: 6, // higher = narrower peak (gaussian exponent)

  // When orbiting orb count is low, stretch wave intervals
  lowOrbThreshold: 6, // below this count, slow down
  lowOrbIntervalMultiplier: 2.5, // multiply wave interval by this

  // Initial quiet period
  waveStartDelay: 3000,

  // ─── Replenishment ──────────────────────────────────────────────────────
  // Triggered by absorption — one orb in for each orb absorbed
  replenishDelay: [800, 2000], // ms after absorption before replacement fades in [min, max]
  // Per-ring population bounds [min, max] — index 0 unused
  // Won't replenish a ring above its max
  orbsPerRing: [
    [0, 0], // ring 0 (unused)
    [1, 1], // ring 1 (innermost, smallest)
    [1, 2], // ring 2
    [2, 3], // ring 3
    [3, 5], // ring 4
    [4, 7], // ring 5 (outermost, largest)
  ],

  // ─── Firm icon pulse ───────────────────────────────────────────────────
  firmBoostPerAbsorb: 0.06,
  firmSpringStiffness: 30,
  firmSpringDamping: 5,

  // Ring entrance animation
  ringDelays: [0.1, 0.18, 0.26, 0.34, 0.42, 0.5],
  ringOpacities: [0.14, 0.11, 0.08, 0.06, 0.05, 0.04],
}

// ─── Utilities ──────────────────────────────────────────────────────────────

const toRad = (deg: number) => (deg * Math.PI) / 180
const randRange = (min: number, max: number) => min + Math.random() * (max - min)
const randInt = (min: number, max: number) => Math.floor(randRange(min, max + 1))
const ORB_HALF = CONFIG.orbSize / 2
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

// ─── Ring geometry ──────────────────────────────────────────────────────────

const rings = Array.from({ length: CONFIG.ringCount }, (_, i) => ({
  radius: Math.round(CONFIG.baseRadius * Math.pow(CONFIG.ringRatio, i)),
}))

// ─── Orb types ──────────────────────────────────────────────────────────────

type OrbState = 'orbiting' | 'matched' | 'approved' | 'accepting'

interface OrbEntry {
  angle: number
  radius: number
  ringIndex: number
  angularSpeed: number
  baseAngularSpeed: number // full orbital speed (before matched slowdown)
  el: HTMLElement
  fadeInStart: number // rAF timestamp for fade-in (0 = already visible)
  // Lifecycle
  state: OrbState
  stateTime: number // rAF timestamp when current state began
  nextTransition: number // rAF timestamp for next state change (0 = none scheduled)
  // Spiral absorption — angular momentum L = ω * r² (conserved)
  angularMomentum: number
  // Tether line from orb to center during accepting
  tetherEl: HTMLElement | null
}

const orbQueue: OrbEntry[] = []

interface DyingOrb {
  el: HTMLElement
  startTime: number
}
const dyingOrbs: DyingOrb[] = []

// ─── Firm icon pulse ────────────────────────────────────────────────────────

let firmEl: HTMLElement | null = null
let firmAnimationCleared = false
let firmScaleBoost = 0
let firmScaleVelocity = 0
const firmRef = ref<HTMLElement | null>(null)

// ─── Initial orb data ───────────────────────────────────────────────────────

const initialOrbData: {
  angle: number
  radius: number
  ringIndex: number
  angularSpeed: number
  fadeInDelay: number
}[] = []

let orbIndex = 0
for (let ringIdx = 1; ringIdx < CONFIG.ringCount; ringIdx++) {
  const count = CONFIG.initialOrbsPerRing[ringIdx]
  const offset = toRad(CONFIG.ringOffsets[ringIdx])
  const r = rings[ringIdx].radius
  const angularSpeed = CONFIG.linearSpeed / r

  for (let j = 0; j < count; j++) {
    initialOrbData.push({
      angle: offset + ((2 * Math.PI) / count) * j,
      radius: r,
      ringIndex: ringIdx,
      angularSpeed,
      fadeInDelay: CONFIG.enterBaseDelay + orbIndex * CONFIG.enterStagger,
    })
    orbIndex++
  }
}

const initialOrbEls: HTMLElement[] = []
let animFrame: number
let startTime: number | null = null
let lastTime: number | null = null
let initialOrbsRegistered = false

// ─── Spring orbs (click-to-spawn) ───────────────────────────────────────────

interface SpringOrb {
  x: number
  y: number
  vx: number
  vy: number
  targetX: number
  targetY: number
  ringRadius: number
  ringIndex: number
  targetAngle: number
  el: HTMLElement
  settled: boolean
}

const springOrbs: SpringOrb[] = []
const containerRef = ref<HTMLElement | null>(null)

// ─── Probability-based matching with wave windows ───────────────────────────

let lastMatchCheck: number = 0 // last time we rolled probabilities
let waveCenter: number = 0 // rAF timestamp of current/next wave peak
let waveDuration: number = 0 // duration of current wave window
let matchingStarted = false

const scheduleNextWave = (now: number) => {
  const orbitingCount = orbQueue.filter((o) => o.state === 'orbiting').length
  let interval = randRange(CONFIG.waveInterval[0], CONFIG.waveInterval[1])

  if (orbitingCount < CONFIG.lowOrbThreshold) {
    interval *= CONFIG.lowOrbIntervalMultiplier
  }

  waveCenter = now + interval
  waveDuration = randRange(CONFIG.waveWindowDuration[0], CONFIG.waveWindowDuration[1])
}

/**
 * Compute current wave boost using a bell curve (gaussian).
 * Returns 0 outside the wave window, peaks at wavePeakBoost at center.
 */
const getWaveBoost = (now: number): number => {
  if (waveCenter === 0) return 0
  const halfWindow = waveDuration / 2
  const dist = now - waveCenter
  if (Math.abs(dist) > halfWindow) return 0
  const t = dist / halfWindow
  return CONFIG.wavePeakBoost * Math.exp(-CONFIG.waveBellTightness * t * t)
}

/**
 * Run probability check on all eligible orbs.
 * Each orb independently rolls against (ambient + waveBoost) * ringWeight.
 */
const runMatchCheck = (now: number) => {
  const boost = getWaveBoost(now)
  const baseChance = CONFIG.ambientMatchChance + boost

  for (let i = 0; i < orbQueue.length; i++) {
    const orb = orbQueue[i]
    if (orb.state !== 'orbiting') continue
    if (orb.stateTime === 0 || now - orb.stateTime < CONFIG.minOrbitTime) continue

    // Weight by ring — inner rings more likely
    const weight = (CONFIG.ringWeights[orb.ringIndex] || 1) / 3 // normalize so weight 3 = 1x
    const chance = baseChance * weight

    if (Math.random() < chance) {
      transitionState(orb, 'matched', now)
      orb.nextTransition = now + randRange(CONFIG.matchedDuration[0], CONFIG.matchedDuration[1])
    }
  }

  // Schedule next wave if current one has passed
  if (now > waveCenter + waveDuration / 2) {
    scheduleNextWave(now)
  }
}

// ─── SVG templates ──────────────────────────────────────────────────────────

// NOTE: This SVG must match the VIcon "User" icon in
// src/modules/shared/components/icons/user.vue — keep them in sync.
const USER_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" vector-effect="non-scaling-stroke"/><circle cx="12" cy="7" r="4" vector-effect="non-scaling-stroke"/></svg>'

// ─── Replenishment system (absorption-triggered) ────────────────────────────

// Pending spawns — scheduled after absorption with a random delay
const pendingSpawns: { spawnTime: number }[] = []

const countOrbitingOnRing = (ringIdx: number) =>
  orbQueue.filter((o) => o.ringIndex === ringIdx && o.state === 'orbiting').length

/**
 * Find the best angle to insert a new orb on a ring by locating the largest
 * angular gap between existing orbs and placing it at the midpoint.
 * Falls back to a random angle if the ring is empty.
 */
const findBestAngle = (ringIdx: number): number => {
  // Collect current angles of all orbs on this ring (any state, still visually present)
  const angles = orbQueue
    .filter((o) => o.ringIndex === ringIdx)
    .map((o) => ((o.angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI))

  if (angles.length === 0) return Math.random() * Math.PI * 2

  // Sort ascending
  angles.sort((a, b) => a - b)

  // Find largest gap (including wrap-around gap between last and first)
  let bestGap = 0
  let bestMid = 0
  for (let i = 0; i < angles.length; i++) {
    const next = i + 1 < angles.length ? angles[i + 1] : angles[0] + 2 * Math.PI
    const gap = next - angles[i]
    if (gap > bestGap) {
      bestGap = gap
      bestMid = angles[i] + gap / 2
    }
  }

  return bestMid
}

const scheduleReplenish = (now: number) => {
  const delay = randRange(CONFIG.replenishDelay[0], CONFIG.replenishDelay[1])
  pendingSpawns.push({ spawnTime: now + delay })
}

const spawnReplenishOrb = (now: number) => {
  const container = containerRef.value
  if (!container) return

  // Find rings below their max
  const available: number[] = []
  for (let i = 1; i < CONFIG.ringCount; i++) {
    const count = countOrbitingOnRing(i)
    const [, max] = CONFIG.orbsPerRing[i]
    if (count < max) available.push(i)
  }

  if (available.length === 0) return

  // Prioritize rings below min, otherwise random from available
  const needy = available.filter((i) => countOrbitingOnRing(i) < CONFIG.orbsPerRing[i][0])
  const pool = needy.length > 0 ? needy : available
  const ringIdx = pool[Math.floor(Math.random() * pool.length)]

  const ringRadius = rings[ringIdx].radius
  const angle = findBestAngle(ringIdx)
  const angularSpeed = CONFIG.linearSpeed / ringRadius

  const el = document.createElement('div')
  el.className = 'orbital-orb'
  el.innerHTML = USER_SVG
  el.style.opacity = '0'
  container.appendChild(el)

  orbQueue.push({
    angle,
    radius: ringRadius,
    ringIndex: ringIdx,
    angularSpeed,
    baseAngularSpeed: angularSpeed,
    el,
    state: 'orbiting',
    stateTime: now,
    nextTransition: 0,
    angularMomentum: 0,
    tetherEl: null,
    fadeInStart: now,
  })
}

// ─── State transitions ──────────────────────────────────────────────────────

const CHECK_BADGE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'

const applyMatchedStyle = (el: HTMLElement) => {
  el.style.border = '1.5px solid #3b88af'
  el.style.boxShadow =
    'inset 0 1px 0 rgba(255,255,255,0.12), 0 0 12px 2px rgba(59,136,175,0.4), 0 2px 8px rgba(0,0,0,0.15)'
}

const applyApprovedStyle = (el: HTMLElement) => {
  const badge = document.createElement('div')
  badge.className = 'orbital-check-badge'
  badge.innerHTML = CHECK_BADGE_SVG
  el.appendChild(badge)
}

const transitionState = (orb: OrbEntry, newState: OrbState, now: number) => {
  orb.state = newState
  orb.stateTime = now

  if (newState === 'matched') {
    // Slow down but keep orbiting
    orb.angularSpeed = orb.baseAngularSpeed * CONFIG.matchedSpeedFactor
    applyMatchedStyle(orb.el)

    // Create tether line from center to orb
    const container = containerRef.value
    if (container) {
      const tether = document.createElement('div')
      tether.className = 'orbital-tether'
      tether.style.width = '0px'
      tether.style.opacity = '0'
      const firmIcon = firmRef.value
      if (firmIcon) {
        container.insertBefore(tether, firmIcon)
      } else {
        container.appendChild(tether)
      }
      orb.tetherEl = tether
    }
  } else if (newState === 'approved') {
    applyApprovedStyle(orb.el)
    orb.nextTransition = now + randRange(CONFIG.acceptingDelay[0], CONFIG.acceptingDelay[1])
  } else if (newState === 'accepting') {
    // Capture angular momentum: L = ω * r² (boosted to exaggerate spiral)
    orb.angularMomentum = orb.angularSpeed * orb.radius * orb.radius * CONFIG.spiralMomentumBoost
    orb.nextTransition = 0
  }
}

const pulseFirm = (now: number) => {
  firmScaleBoost += CONFIG.firmBoostPerAbsorb

  const container = containerRef.value
  if (!container) return
  const el = document.createElement('div')
  el.className = 'orbital-ripple'
  el.addEventListener('animationend', () => el.remove(), { once: true })
  container.appendChild(el)

  // Schedule a replacement orb
  scheduleReplenish(now)
}

// ─── Eviction ───────────────────────────────────────────────────────────────

const evictOldest = (now: number) => {
  // Only evict orbiting orbs, skip orbs mid-lifecycle
  const idx = orbQueue.findIndex((o) => o.state === 'orbiting')
  if (idx === -1) return
  const victim = orbQueue.splice(idx, 1)[0]
  dyingOrbs.push({ el: victim.el, startTime: now })
}

// ─── Spawn (click-to-add) ───────────────────────────────────────────────────

const findClosestRing = (distFromCenter: number): number => {
  let closest = 1
  let minDiff = Math.abs(rings[1].radius - distFromCenter)
  for (let i = 2; i < rings.length; i++) {
    const diff = Math.abs(rings[i].radius - distFromCenter)
    if (diff < minDiff) {
      minDiff = diff
      closest = i
    }
  }
  return closest
}

const spawnOrb = (clientX: number, clientY: number) => {
  const container = containerRef.value
  if (!container) return

  const rect = container.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  const x = clientX - centerX
  const y = clientY - centerY

  const dist = Math.sqrt(x * x + y * y)
  const ringIdx = findClosestRing(dist)
  const ringRadius = rings[ringIdx].radius

  const angle = Math.atan2(y, x)
  const targetX = Math.cos(angle) * ringRadius
  const targetY = Math.sin(angle) * ringRadius

  const el = document.createElement('div')
  el.className = 'orbital-orb'
  el.innerHTML = USER_SVG
  el.style.transform = `translate3d(${x - ORB_HALF}px,${y - ORB_HALF}px,0)`
  el.style.opacity = '1'
  container.appendChild(el)

  springOrbs.push({
    x,
    y,
    vx: 0,
    vy: 0,
    targetX,
    targetY,
    ringRadius,
    ringIndex: ringIdx,
    targetAngle: angle,
    el,
    settled: false,
  })
}

const onMouseUp = (e: MouseEvent) => {
  if (prefersReducedMotion.matches) return
  spawnOrb(e.clientX, e.clientY)
}

// ─── Static display for reduced motion ──────────────────────────────────────

const showStaticOrbs = () => {
  if (initialOrbEls.length !== initialOrbData.length) return

  for (let i = 0; i < initialOrbData.length; i++) {
    const data = initialOrbData[i]
    const el = initialOrbEls[i]
    const x = Math.cos(data.angle) * data.radius - ORB_HALF
    const y = Math.sin(data.angle) * data.radius - ORB_HALF
    el.style.transform = `translate3d(${x}px,${y}px,0)`
    el.style.opacity = '1'
  }
}

// ─── Main animation loop ────────────────────────────────────────────────────

const tick = (now: number) => {
  if (!startTime) startTime = now
  const dt = lastTime ? (now - lastTime) / 1000 : 0
  lastTime = now

  const clampedDt = Math.min(dt, 0.05)

  // Grab firm element ref once
  if (!firmEl && firmRef.value) firmEl = firmRef.value

  // One-time: register initial orbs into queue
  if (!initialOrbsRegistered && initialOrbEls.length === initialOrbData.length) {
    for (let i = 0; i < initialOrbData.length; i++) {
      const data = initialOrbData[i]
      orbQueue.push({
        angle: data.angle,
        radius: data.radius,
        ringIndex: data.ringIndex,
        angularSpeed: data.angularSpeed,
        baseAngularSpeed: data.angularSpeed,
        el: initialOrbEls[i],
        fadeInStart: startTime! + data.fadeInDelay,
        state: 'orbiting',
        stateTime: now,
        nextTransition: 0,
        angularMomentum: 0,
        tetherEl: null,
      })
    }
    initialOrbsRegistered = true

    // Schedule first wave after quiet period
    scheduleNextWave(now + CONFIG.waveStartDelay)
    matchingStarted = true
  }

  // ── Probability-based matching ──
  if (matchingStarted && now - lastMatchCheck >= CONFIG.matchCheckInterval) {
    lastMatchCheck = now
    runMatchCheck(now)
  }

  // ── Pending replenishment spawns ──
  for (let i = pendingSpawns.length - 1; i >= 0; i--) {
    if (now >= pendingSpawns[i].spawnTime) {
      spawnReplenishOrb(now)
      pendingSpawns.splice(i, 1)
    }
  }

  // ── Animate all queued orbs ──
  for (let i = orbQueue.length - 1; i >= 0; i--) {
    const orb = orbQueue[i]
    if (!orb.el) continue

    // Staggered entrance — skip orbs whose fade-in hasn't started yet
    if (orb.fadeInStart > 0 && now < orb.fadeInStart) continue

    // Check scheduled state transitions
    if (orb.nextTransition > 0 && now >= orb.nextTransition) {
      if (orb.state === 'matched') {
        transitionState(orb, 'approved', now)
      } else if (orb.state === 'approved') {
        transitionState(orb, 'accepting', now)
      }
    }

    // Handle accepting state — spiral inward with angular momentum conservation
    if (orb.state === 'accepting') {
      // Pull accelerates as orb approaches center (lerp from base to base+accel)
      const startRadius = rings[orb.ringIndex]?.radius || 200
      const proximity = 1 - Math.min(1, orb.radius / startRadius) // 0 at ring, 1 at center
      const pullSpeed = CONFIG.spiralPullBase + CONFIG.spiralPullAccel * proximity
      orb.radius -= pullSpeed * clampedDt

      // Angular momentum conservation: ω = L / r², capped to avoid frenzy
      if (orb.radius > 1) {
        orb.angularSpeed = Math.min(orb.angularMomentum / (orb.radius * orb.radius), CONFIG.spiralMaxAngularSpeed)
      }

      // Advance angle (spins faster as radius shrinks)
      orb.angle += orb.angularSpeed * clampedDt

      const x = Math.cos(orb.angle) * orb.radius - ORB_HALF
      const y = Math.sin(orb.angle) * orb.radius - ORB_HALF

      if (orb.radius < CONFIG.spiralFadeRadius) {
        const t = Math.max(0, orb.radius / CONFIG.spiralFadeRadius)
        orb.el.style.transform = `translate3d(${x}px,${y}px,0) scale(${t})`
        orb.el.style.opacity = String(t)
      } else {
        orb.el.style.transform = `translate3d(${x}px,${y}px,0)`
      }

      if (orb.radius < 5) {
        if (orb.tetherEl) orb.tetherEl.remove()
        orb.el.remove()
        orbQueue.splice(i, 1)
        pulseFirm(now)
        continue
      }
    } else {
      // Normal orbiting (matched/approved keep moving at their speed)
      orb.angle += orb.angularSpeed * clampedDt

      const x = Math.cos(orb.angle) * orb.radius - ORB_HALF
      const y = Math.sin(orb.angle) * orb.radius - ORB_HALF

      // Fade in
      let opacity = 1
      if (orb.fadeInStart > 0) {
        opacity = Math.min(1, (now - orb.fadeInStart) / CONFIG.enterFadeDuration)
      }

      orb.el.style.transform = `translate3d(${x}px,${y}px,0)`
      orb.el.style.opacity = String(opacity)
    }

    // Update tether line (present during matched, approved, and accepting)
    if (orb.tetherEl) {
      const dist = orb.radius
      const angleDeg = (orb.angle * 180) / Math.PI
      const ringRadius = rings[orb.ringIndex]?.radius || 200
      const tetherOpacity = Math.min(0.6, dist / (ringRadius * 0.8))
      orb.tetherEl.style.width = `${dist}px`
      orb.tetherEl.style.transform = `rotate(${angleDeg}deg)`
      orb.tetherEl.style.opacity = String(tetherOpacity)
    }
  }

  // ── Animate spring orbs (click-to-spawn, not yet settled) ──
  for (let i = springOrbs.length - 1; i >= 0; i--) {
    const orb = springOrbs[i]
    if (orb.settled) continue

    const dx = orb.x - orb.targetX
    const dy = orb.y - orb.targetY
    const ax = -CONFIG.spawnStiffness * dx - CONFIG.spawnDamping * orb.vx
    const ay = -CONFIG.spawnStiffness * dy - CONFIG.spawnDamping * orb.vy

    orb.vx += ax * clampedDt
    orb.vy += ay * clampedDt
    orb.x += orb.vx * clampedDt
    orb.y += orb.vy * clampedDt

    orb.el.style.transform = `translate3d(${orb.x - ORB_HALF}px,${orb.y - ORB_HALF}px,0)`

    const dist = Math.sqrt(dx * dx + dy * dy)
    const speed = Math.sqrt(orb.vx * orb.vx + orb.vy * orb.vy)
    if (dist < CONFIG.spawnSettleThreshold && speed < CONFIG.spawnSettleThreshold) {
      orb.settled = true

      const graduatedSpeed = CONFIG.linearSpeed / orb.ringRadius
      orbQueue.push({
        angle: orb.targetAngle,
        radius: orb.ringRadius,
        ringIndex: orb.ringIndex,
        angularSpeed: graduatedSpeed,
        baseAngularSpeed: graduatedSpeed,
        el: orb.el,
        fadeInStart: 0,
        state: 'orbiting',
        stateTime: now,
        nextTransition: 0,
        angularMomentum: 0,
        tetherEl: null,
      })

      springOrbs.splice(i, 1)

      while (orbQueue.length > CONFIG.maxOrbs) {
        evictOldest(now)
      }
    }
  }

  // ── Animate dying orbs (eviction fade-out) ──
  for (let i = dyingOrbs.length - 1; i >= 0; i--) {
    const dying = dyingOrbs[i]
    const fadeElapsed = now - dying.startTime
    const opacity = 1 - fadeElapsed / CONFIG.evictionFadeMs

    if (opacity <= 0) {
      dying.el.remove()
      dyingOrbs.splice(i, 1)
    } else {
      dying.el.style.opacity = String(opacity)
    }
  }

  // ── Firm icon ──
  if (firmEl && !firmAnimationCleared && now - startTime! > 1200) {
    firmEl.style.animation = 'none'
    firmAnimationCleared = true
  }

  if (firmEl && firmAnimationCleared) {
    const ax = -CONFIG.firmSpringStiffness * firmScaleBoost - CONFIG.firmSpringDamping * firmScaleVelocity
    firmScaleVelocity += ax * clampedDt
    firmScaleBoost += firmScaleVelocity * clampedDt

    if (Math.abs(firmScaleBoost) < 0.001 && Math.abs(firmScaleVelocity) < 0.001) {
      firmScaleBoost = 0
      firmScaleVelocity = 0
    }

    firmEl.style.transform = `translate3d(0,0,0) scale(${1 + firmScaleBoost})`
  }

  animFrame = requestAnimationFrame(tick)
}

const setOrbRef = (el: any) => {
  if (el) initialOrbEls.push(el)
}

onMounted(() => {
  if (prefersReducedMotion.matches) {
    showStaticOrbs()
    return
  }
  animFrame = requestAnimationFrame(tick)
})

onUnmounted(() => {
  if (animFrame) cancelAnimationFrame(animFrame)

  // Remove dynamically created elements (Vue handles template-rendered ones)
  // This includes tether lines attached to accepting orbs
  for (const orb of orbQueue) {
    if (!orb.el.parentElement) continue
    // Only remove dynamically created orbs, not template-rendered initial ones
    if (!initialOrbEls.includes(orb.el)) orb.el.remove()
    if (orb.tetherEl) orb.tetherEl.remove()
  }
  for (const spring of springOrbs) {
    spring.el.remove()
  }
  for (const dying of dyingOrbs) {
    dying.el.remove()
  }

  // Clear arrays
  orbQueue.length = 0
  springOrbs.length = 0
  dyingOrbs.length = 0
  pendingSpawns.length = 0
  initialOrbEls.length = 0

  // Reset module-level state for clean remount
  startTime = null
  lastTime = null
  initialOrbsRegistered = false
  matchingStarted = false
  lastMatchCheck = 0
  waveCenter = 0
  waveDuration = 0
  firmEl = null
  firmAnimationCleared = false
  firmScaleBoost = 0
  firmScaleVelocity = 0
})
</script>

<template>
  <div ref="containerRef" class="relative flex h-full items-center justify-center" @mouseup="onMouseUp">
    <!-- Concentric rings -->
    <div
      v-for="(ring, i) in rings"
      :key="i"
      class="orbital-ring"
      :style="{
        width: ring.radius * 2 + 'px',
        height: ring.radius * 2 + 'px',
        border: '1px solid rgba(255,255,255,' + CONFIG.ringOpacities[i] + ')',
        animationDelay: CONFIG.ringDelays[i] + 's',
      }"
    />

    <!-- Initial investor orbs — positioned via direct DOM writes in rAF -->
    <div v-for="(_, i) in initialOrbData" :key="i" :ref="setOrbRef" class="orbital-orb" style="opacity: 0">
      <VIcon name="User" class="h-5 w-5 text-white/50" />
    </div>

    <!-- Central firm icon -->
    <div
      ref="firmRef"
      class="orbital-firm relative z-10 flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#3b88af] shadow-[0_0_60px_20px_rgba(59,136,175,0.25)]"
    >
      <Briefcase :size="36" :stroke-width="1.5" class="text-white" />
    </div>
  </div>
</template>

<style>
/* UNSCOPED intentionally — dynamically created elements (spawned orbs, ripples,
   check badges) are added via document.createElement and need these class styles.
   Adding `scoped` would break them because scoped styles only apply to
   template-rendered elements. */

/* ─── Ring & center animations ─── */

.orbital-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 9999px;
  animation: orbitalScaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.orbital-firm {
  animation: orbitalPopIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s both;
  will-change: transform;
}

/* ─── Keyframes ─── */

@keyframes orbitalScaleIn {
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes orbitalPopIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .orbital-ring,
  .orbital-firm {
    animation: none;
  }
}

/* ─── Investor orbs ─── */

.orbital-orb {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  will-change: transform, opacity;
  transition:
    border 0.3s ease,
    box-shadow 0.3s ease;
}

/* ─── Checkmark badge ─── */

.orbital-check-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b88af;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: orbitalBadgePop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes orbitalBadgePop {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ─── Tether line (orb → center during absorption) ─── */

.orbital-tether {
  position: absolute;
  left: 50%;
  top: 50%;
  height: 2px;
  margin-top: -1px;
  transform-origin: 0 center;
  background: linear-gradient(90deg, rgba(59, 136, 175, 0.6) 0%, rgba(59, 136, 175, 0) 100%);
  pointer-events: none;
  z-index: 4;
  will-change: transform, width, opacity;
}

/* ─── Absorb ripple glow (pure CSS, GPU-composited) ─── */

.orbital-ripple {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 136, 175, 0.5) 0%, rgba(59, 136, 175, 0) 70%);
  pointer-events: none;
  z-index: 9;
  will-change: transform, opacity;
  animation: orbitalRipple 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes orbitalRipple {
  from {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.4;
  }
  to {
    transform: translate(-50%, -50%) scale(4);
    opacity: 0;
  }
}
</style>
